import type { ReactElement, ReactNode } from "react";
import { CalendarRange, Gauge } from "lucide-react";
import type { ProblemTimelineParams } from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import { timelineSummary } from "./presentation.js";

/**
 * The three derived headline readings — suspect window, exposure, certainty
 * mix. Presentation only: every value is recomputed from the saved milestones,
 * nothing here is stored.
 */
export function TimelineSummaryStrip({
  data,
  variant,
}: {
  data: ProblemTimelineParams;
  variant: "plain" | "bar";
}): ReactElement {
  const summary = timelineSummary(data);

  return (
    <div
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2",
        variant === "bar" &&
          "rounded-lg border border-pb-border bg-pb-card-elevated/40 px-3 py-2",
      )}
    >
      <SummaryItem icon={<CalendarRange className="h-4 w-4" />}>
        {summary.days === null ? (
          <span className="text-pb-text-muted">Suspect window not sized</span>
        ) : (
          <>
            <strong className="font-semibold text-pb-amber-text">
              {summary.days}-day
            </strong>{" "}
            suspect window
          </>
        )}
      </SummaryItem>
      <SummaryItem icon={<Gauge className="h-4 w-4" />}>
        {summary.exposure === null ? (
          <span className="text-pb-text-muted">Exposure not sized</span>
        ) : (
          <>
            <strong className="font-semibold text-pb-amber-text">
              ≈{summary.exposure.toLocaleString("en-GB")}
            </strong>{" "}
            {summary.exposureUnit} exposed
          </>
        )}
      </SummaryItem>
    </div>
  );
}

function SummaryItem({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs text-pb-text-secondary">
      <span className="shrink-0 text-pb-text-muted" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}
